'use client';

import { forwardRef, useId } from 'react';
import { css } from '@/styled-system/css';

// Not `srOnly`, `display: none` or `readOnly`: each stops a phone opening its keyboard. Fixed, so
// focus cannot scroll the page; `body` is 16px, the size below which iOS zooms to a field.
const keyboardProxy = css({
  position: 'fixed',
  top: '0',
  left: '0',
  width: '1px', // eslint-disable-line hotpads/no-magic-numbers, constellation/no-raw-spacing-values -- the smallest box that still takes focus
  height: '1px', // eslint-disable-line hotpads/no-magic-numbers, constellation/no-raw-spacing-values -- the smallest box that still takes focus
  opacity: '0',
  fontSize: 'body',
  pointerEvents: 'none',
});

/**
 * An invisible input that holds a phone's keyboard open while a dialog mounts.
 *
 * A phone opens its keyboard only for focus given inside the tap, and a dialog's field attaches
 * after it. Focus this in the tap; the dialog's field takes focus from it and the keyboard stays.
 * Point the dialog's `returnFocus` at the control that opened it, or closing focuses this again.
 */
export const KeyboardProxyInput = forwardRef<HTMLInputElement>(function KeyboardProxyInput(_props, ref) {
  // Chrome DevTools flags a form field with neither an id nor a name (HP-9041).
  const id = useId();
  return (
    <input
      ref={ref}
      id={id}
      className={keyboardProxy}
      // The same keyboard layout as the search fields it hands off to.
      type="search"
      enterKeyHint="search"
      autoComplete="off"
      aria-hidden="true"
      tabIndex={-1}
      // Inside ListingSearch's <form>, where Enter before the dialog's field takes focus would submit it.
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.preventDefault();
      }}
      data-testid="keyboard-proxy"
    />
  );
});

/** A dialog field's ref: focus in the attaching commit, as `initialFocus` waits a frame and the keyboard closes. */
export function focusOnAttach(node: HTMLElement | null) {
  node?.focus();
}
